import React, { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Mail, Phone, MessageCircle, Instagram, MapPin, Clock, ChevronDown, Send, Check } from "lucide-react";
import { api } from "@/lib/api";
import Reveal from "@/components/Reveal";
import SeoHead from "@/components/SeoHead";
import JsonLd, { buildBreadcrumbSchema, buildWebPageSchema, buildFaqSchema } from "@/components/JsonLd";
import { toast } from "sonner";

const Contact = () => {
  const [data, setData] = useState(null);
  const [form, setForm] = useState({ name: "", email: "", phone: "", subject: "", message: "" });
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);
  const [openFaq, setOpenFaq] = useState(null);

  useEffect(() => {
    api.get("/pages/contact").then((r) => {
      setData(r.data);
      setForm((f) => ({ ...f, subject: (r.data.subjects && r.data.subjects[0]) || "General enquiry" }));
    }).catch(() => {});
  }, []);

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!form.email || !form.message) { toast.error("Email and message are required"); return; }
    setSending(true);
    try {
      await api.post("/contact/messages", form);
      setSent(true);
      toast.success("Message sent — we'll write back within one business day.");
      setForm({ name: "", email: "", phone: "", subject: (data?.subjects && data.subjects[0]) || "General enquiry", message: "" });
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not send message");
    } finally { setSending(false); }
  };

  if (!data) return <div className="min-h-[60vh] flex items-center justify-center text-mauve">Loading…</div>;

  const cards = [
    { icon: Mail,          label: "Email us",       value: data.email,     href: `mailto:${data.email}`,                     tint: "#F3D6E2", accent: "#C9789A", note: "We reply within 24h" },
    { icon: Phone,         label: "Call",           value: data.phone,     href: `tel:${(data.phone || "").replace(/\s/g, "")}`, tint: "#E6E2F5", accent: "#8E53AC", note: "Mon–Sat 10am–7pm IST" },
    { icon: MessageCircle, label: "WhatsApp",       value: data.whatsapp,  href: `https://wa.me/${(data.whatsapp || "").replace(/\D/g, "")}`, tint: "#D6F0E2", accent: "#5E9F7A", note: "Fastest response" },
    { icon: Instagram,     label: "Instagram DM",   value: "@rieave",      href: data.instagram,                              tint: "#FCE4C4", accent: "#D4B896", note: "Slide into our DMs" },
  ];

  return (
    <div className="font-body text-charcoal" data-testid="contact-page">
      <SeoHead
        title={data.seo_title || data.title || "Contact — Rieave"}
        description={data.seo_description || data.hero_subtitle || "Reach our team for orders, sizing, custom requests, or simply to say hi."}
        keywords={data.seo_keywords}
        image={data.og_image}
        canonical={typeof window !== "undefined" ? window.location.origin + "/contact" : "/contact"}
        robots={data.seo_robots}
      />
      <JsonLd
        data={[
          buildBreadcrumbSchema([
            { name: "Home", url: typeof window !== "undefined" ? window.location.origin + "/" : "/" },
            { name: "Contact", url: typeof window !== "undefined" ? window.location.origin + "/contact" : "/contact" },
          ]),
          buildWebPageSchema({
            name: data.seo_title || data.title || "Contact",
            description: data.seo_description || data.hero_subtitle,
            url: typeof window !== "undefined" ? window.location.origin + "/contact" : "/contact",
            type: "ContactPage",
          }),
          buildFaqSchema(data.faqs || []),
        ].filter(Boolean)}
      />
      {/* HERO — asymmetric with floating form anchor */}
      <section
        className="relative overflow-hidden"
        style={{ background: "radial-gradient(ellipse at top right, rgba(201,120,154,0.18), transparent 60%), radial-gradient(ellipse at bottom left, rgba(212,184,150,0.18), transparent 60%), #FBF7F4" }}
        data-testid="contact-hero"
      >
        <div className="rieave-container pt-28 md:pt-36 pb-16 md:pb-20">
          <div className="max-w-3xl">
            <motion.p className="eyebrow mb-5" initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}>
              {data.hero_eyebrow || "Say hello"}
            </motion.p>
            <motion.h1
              className="font-hero text-5xl md:text-7xl lg:text-[5.5rem] leading-[0.95]"
              initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.9, ease: [0.22, 1, 0.36, 1] }}
            >
              {(data.hero_title || "").split(" ").slice(0, -2).join(" ")}{" "}
              <span className="serif-italic text-rose">{(data.hero_title || "").split(" ").slice(-2).join(" ")}</span>
            </motion.h1>
            <motion.p className="mt-7 text-lg text-charcoal/70 max-w-xl" initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.15, duration: 0.8 }}>
              {data.hero_subtitle}
            </motion.p>
          </div>
        </div>
      </section>

      {/* CONTACT CARDS — 4 interactive tiles */}
      <section className="relative -mt-4 pb-16" data-testid="contact-cards">
        <div className="rieave-container grid grid-cols-2 lg:grid-cols-4 gap-3 md:gap-4">
          {cards.map((c, i) => {
            const Icon = c.icon;
            return (
              <Reveal key={i} delay={i * 0.06}>
                <a
                  href={c.href}
                  target={c.href?.startsWith("http") ? "_blank" : undefined}
                  rel="noreferrer"
                  className="group relative block bg-cream border border-charcoal/8 rounded-[2px] p-6 md:p-7 overflow-hidden transition-transform hover:-translate-y-1"
                  data-testid={`contact-card-${c.label.toLowerCase().replace(/\s+/g, "-")}`}
                >
                  <div className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-500" style={{ background: `linear-gradient(135deg, ${c.tint}, transparent 70%)` }} />
                  <div className="relative">
                    <div className="w-11 h-11 rounded-full flex items-center justify-center mb-5 transition-transform group-hover:scale-110" style={{ background: c.tint }}>
                      <Icon size={18} style={{ color: c.accent }} />
                    </div>
                    <div className="eyebrow mb-1">{c.label}</div>
                    <div className="font-cormorant text-xl md:text-2xl text-charcoal leading-tight break-all">{c.value}</div>
                    <div className="mt-4 text-xs text-charcoal/50">{c.note}</div>
                  </div>
                  <div className="absolute bottom-0 left-0 right-0 h-[2px] origin-left scale-x-0 group-hover:scale-x-100 transition-transform duration-500" style={{ background: c.accent }} />
                </a>
              </Reveal>
            );
          })}
        </div>
      </section>

      {/* FORM + MAP split */}
      <section className="bg-cream border-t border-b border-charcoal/5" data-testid="contact-form-section">
        <div className="rieave-container grid lg:grid-cols-12 gap-0">
          {/* Form */}
          <div className="lg:col-span-7 py-16 md:py-24 lg:pr-16">
            <Reveal>
              <p className="eyebrow mb-3">Write to us</p>
              <h2 className="font-hero text-4xl md:text-5xl mb-3">Drop us a note.</h2>
              <p className="text-charcoal/65 mb-10">We read every message personally — no bots, no form letters.</p>
            </Reveal>

            <AnimatePresence mode="wait">
              {sent ? (
                <motion.div
                  key="sent"
                  initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }}
                  className="p-8 border border-rose/30 bg-rose/5 rounded-[2px]"
                  data-testid="contact-form-success"
                >
                  <div className="w-12 h-12 rounded-full bg-rose/15 flex items-center justify-center mb-5">
                    <Check size={22} className="text-rose" />
                  </div>
                  <h3 className="font-hero text-2xl mb-2">Thank you — your note is on its way.</h3>
                  <p className="text-charcoal/70">We usually reply within one business day. In the meantime, feel free to browse the edit.</p>
                  <button onClick={() => setSent(false)} className="mt-6 btn-link" data-testid="contact-form-reset">Send another message →</button>
                </motion.div>
              ) : (
                <motion.form
                  key="form"
                  onSubmit={onSubmit}
                  className="space-y-6"
                  initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
                  data-testid="contact-form"
                >
                  <div className="grid md:grid-cols-2 gap-6">
                    <Field label="Your name" value={form.name} onChange={(v) => setForm({ ...form, name: v })} testId="contact-name" />
                    <Field label="Email" type="email" required value={form.email} onChange={(v) => setForm({ ...form, email: v })} testId="contact-email" />
                  </div>
                  <div className="grid md:grid-cols-2 gap-6">
                    <Field label="Phone (optional)" value={form.phone} onChange={(v) => setForm({ ...form, phone: v })} testId="contact-phone" />
                    <div>
                      <label className="block text-[11px] uppercase tracking-[0.24em] text-mauve mb-2">Subject</label>
                      <div className="relative">
                        <select
                          value={form.subject}
                          onChange={(e) => setForm({ ...form, subject: e.target.value })}
                          className="w-full bg-transparent border-b border-charcoal/20 py-3 pr-8 text-charcoal outline-none focus:border-rose appearance-none font-body"
                          data-testid="contact-subject"
                        >
                          {(data.subjects || []).map((s) => <option key={s} value={s}>{s}</option>)}
                        </select>
                        <ChevronDown size={14} className="absolute right-0 top-1/2 -translate-y-1/2 text-mauve pointer-events-none" />
                      </div>
                    </div>
                  </div>
                  <div>
                    <label className="block text-[11px] uppercase tracking-[0.24em] text-mauve mb-2">Your message</label>
                    <textarea
                      required rows={5}
                      value={form.message}
                      onChange={(e) => setForm({ ...form, message: e.target.value })}
                      className="w-full bg-transparent border-b border-charcoal/20 py-3 text-charcoal outline-none focus:border-rose resize-none font-body"
                      placeholder="Tell us what's on your mind…"
                      data-testid="contact-message"
                    />
                  </div>
                  <motion.button
                    whileHover={{ scale: 1.02 }} whileTap={{ scale: 0.98 }}
                    type="submit" disabled={sending}
                    className="inline-flex items-center gap-3 rounded-full px-8 py-3.5 text-[12px] uppercase tracking-[0.24em] font-medium text-cream transition-all"
                    style={{ background: "linear-gradient(135deg, #3D3140, #5a4752)" }}
                    data-testid="contact-submit"
                  >
                    {sending ? "Sending…" : <>Send message <Send size={14} /></>}
                  </motion.button>
                </motion.form>
              )}
            </AnimatePresence>
          </div>

          {/* Aside — Studio info + map */}
          <aside className="lg:col-span-5 lg:border-l border-charcoal/8 py-16 md:py-24 lg:pl-16 bg-[#FBF7F4]">
            <Reveal>
              <p className="eyebrow mb-3">The studio</p>
              <h3 className="font-hero text-3xl mb-8">Come say hi.</h3>
              <div className="space-y-6">
                <InfoRow icon={MapPin} title="Visit us" body={data.address} />
                <InfoRow icon={Clock} title="Hours" body={data.hours} />
              </div>
              {data.map_embed && (
                <div className="mt-10 aspect-[4/3] rounded-[2px] overflow-hidden border border-charcoal/10" data-testid="contact-map">
                  <iframe
                    title="rieave studio map"
                    src={data.map_embed}
                    loading="lazy"
                    referrerPolicy="no-referrer-when-downgrade"
                    className="w-full h-full border-0"
                  />
                </div>
              )}
            </Reveal>
          </aside>
        </div>
      </section>

      {/* FAQ — accordion */}
      {data.faqs && data.faqs.length > 0 && (
        <section className="bg-[#FBF7F4]" data-testid="contact-faq">
          <div className="rieave-container py-24 md:py-32 grid md:grid-cols-12 gap-12">
            <div className="md:col-span-4">
              <Reveal>
                <p className="eyebrow mb-3">Frequently asked</p>
                <h2 className="font-hero text-4xl md:text-5xl leading-[1.05]">
                  Quick <span className="serif-italic text-rose">answers.</span>
                </h2>
                <p className="mt-5 text-charcoal/65">Can't find what you need? Write to us above — we reply fast.</p>
              </Reveal>
            </div>
            <div className="md:col-span-8">
              <div className="divide-y divide-charcoal/10 border-t border-b border-charcoal/10">
                {data.faqs.map((f, i) => {
                  const open = openFaq === i;
                  return (
                    <div key={i} data-testid={`contact-faq-${i}`}>
                      <button
                        className="w-full flex items-center justify-between py-6 text-left group"
                        onClick={() => setOpenFaq(open ? null : i)}
                      >
                        <span className="font-cormorant text-xl md:text-2xl text-charcoal pr-6 group-hover:text-rose transition-colors">{f.q}</span>
                        <motion.span animate={{ rotate: open ? 45 : 0 }} transition={{ duration: 0.3 }} className="flex-shrink-0 w-8 h-8 rounded-full border border-charcoal/20 flex items-center justify-center text-lg text-charcoal">
                          +
                        </motion.span>
                      </button>
                      <AnimatePresence initial={false}>
                        {open && (
                          <motion.div
                            initial={{ height: 0, opacity: 0 }}
                            animate={{ height: "auto", opacity: 1 }}
                            exit={{ height: 0, opacity: 0 }}
                            transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
                            className="overflow-hidden"
                          >
                            <p className="pb-6 pr-10 text-charcoal/70 leading-[1.7]">{f.a}</p>
                          </motion.div>
                        )}
                      </AnimatePresence>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        </section>
      )}
    </div>
  );
};

const Field = ({ label, value, onChange, type = "text", required, testId }) => (
  <div>
    <label className="block text-[11px] uppercase tracking-[0.24em] text-mauve mb-2">{label}</label>
    <input
      type={type} required={required} value={value} onChange={(e) => onChange(e.target.value)}
      className="w-full bg-transparent border-b border-charcoal/20 py-3 text-charcoal outline-none focus:border-rose font-body"
      data-testid={testId}
    />
  </div>
);

const InfoRow = ({ icon: Icon, title, body }) => (
  <div className="flex gap-4">
    <div className="w-10 h-10 rounded-full flex-shrink-0 flex items-center justify-center" style={{ background: "#F3D6E2" }}>
      <Icon size={16} className="text-rose" />
    </div>
    <div>
      <div className="eyebrow mb-1">{title}</div>
      <div className="font-cormorant text-lg text-charcoal leading-snug whitespace-pre-line">{body}</div>
    </div>
  </div>
);

export default Contact;
